import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Button, Divider, Row, Col, Select, Alert, Spin, Switch, Mentions, message } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import Axios from "axios";
import jwt from "jsonwebtoken";
import BasicAuthForm from "../GraphQLForm/BasicAuthForm";
import AuthOForm from "../GraphQLForm/AuthOForm";
import SessionBaseAuthForm from "./SessionBaseAuthForm";
import JWTAuthForm from "./JWTAuthForm";
import { useDispatch } from "react-redux";
import { getResourcesAsync } from "../../../redux/resourceSlice";
import MaterialDesignIcon from "../../common/MaterialDesignIcon";
import DateConfigSelect from "./DateConfigSelect";
// import { testRestApiConnection } from '../../api/connectRestAPI'

const { Option } = Mentions;

const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;
const RestApiForm = (props) => {
  const dispatch = useDispatch();
  const [apiName, setApiName] = useState("");
  const [apiNameError, setApiNameError] = useState(false);
  const [apiUrl, setApiUrl] = useState("");
  const [apiTestStatus, setApiTestStatus] = useState("");
  const [apiSaveStatus, setApiSaveStatus] = useState("");
  const [urlParams, setUrlParams] = useState([{ key: "", value: "", dataType: "string" }]);
  const [headers, setHeaders] = useState([{ key: "", value: "", dataType: "string" }]);
  const [bodyParams, setBodyParams] = useState([{ key: "", value: "", dataType: "string" }]);
  const [authMethod, setauthMethod] = useState("none");
  const [httpMethod, setHttpMethod] = useState("get");
  const [authParams, setAuthParams] = useState(null);
  const [isShareable, setIsShareable] = useState(false);
  const [userIdList, setUserIdList] = useState([]);
  const [isWitmegApi, setIsWitmegApi] = useState(true);
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [visibleTo, setVisibleTo] = useState("global");
  const [apiUrlError, setApiUrlError] = useState(null);
  const [authFormValid, setAuthFormValid] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Add states for tracking changes and initial values
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  const [urlParamsErrors, setUrlParamsErrors] = useState([]);
  const [headersErrors, setHeadersErrors] = useState([]);
  const [bodyParamsErrors, setBodyParamsErrors] = useState([]);

  const [resourceNameValidation, setResourceNameValidation] = useState({
    status: "",
    message: "",
  });
  const [debounceTimer, setDebounceTimer] = useState(null);

  const [cursorPosition, setCursorPosition] = useState(null);

  const resourceNameRef = useRef(null);

  const [form] = Form.useForm();

  // Values are stored typed, so `0` and `false` are legitimate entries — never test them for
  // truthiness, only for null/undefined/blank
  const toText = (val) => (val === null || val === undefined ? "" : val.toString());

  // Drop rows the user never filled in — the trailing blank row must not be shown as saved data.
  // A row carrying a dateConfig counts as filled in even though its value is blank.
  const withoutEmptyPairs = (pairs) =>
    (Array.isArray(pairs) ? pairs : []).filter(
      (pair) => toText(pair.key).trim() !== "" || toText(pair.value).trim() !== "" || Boolean(pair.dateConfig),
    );

  const validateDataTypes = (pairs) => {
    return pairs.map((pair) => {
      const hasKey = toText(pair.key).trim() !== "";
      const hasValue = toText(pair.value).trim() !== "";

      // Skip validation if it's an empty row
      if (!hasKey && !hasValue && !pair.dateConfig) {
        return { field: null, message: "" };
      }

      // For date type: no datatype validation needed (handled by dateConfig)
      if (pair.dataType === "date") {
        return { field: null, message: "" };
      }

      // Skip if missing key or value (already handled by validateKeyValuePairs)
      if ((hasKey && !hasValue) || (!hasKey && hasValue)) {
        return { field: null, message: "" };
      }

      // Only validate datatype if both key and value exist
      if (!hasKey || !hasValue) {
        return { field: null, message: "" };
      }

      const value = toText(pair.value).trim();
      const dataType = pair.dataType;

      switch (dataType) {
        case "number":
          const numberRegex = /^-?\d+(\.\d+)?$/;
          if (!numberRegex.test(value)) {
            return {
              field: "value",
              message: "Must be a valid number (integer or decimal)",
            };
          }
          break;

        case "boolean":
          const validBooleans = ["true", "false", "1", "0"];
          if (!validBooleans.includes(value.toLowerCase())) {
            return {
              field: "value",
              message: "Must be true/false or 1/0",
            };
          }
          break;

        case "string":
          try {
            encodeURIComponent(value);
          } catch (e) {
            return {
              field: "value",
              message: "Must be valid UTF-8 text",
            };
          }
          break;

        default:
          break;
      }

      return { field: null, message: "" };
    });
  };

  const validateKeyValuePairs = (pairs) => {
    return pairs.map((pair, index) => {
      const hasKey = toText(pair.key).trim() !== "";
      const hasValue = toText(pair.value).trim() !== "";

      // Skip validation if it's completely empty row
      if (!hasKey && !hasValue && !pair.dateConfig) {
        return { field: null, message: "" };
      }

      /* A key used twice in the same list is always broken, and silently so: the
         request body is built as `obj[key] = value`, so the second row overwrites the
         first and one of the two values simply never leaves the browser. A query
         string keeps both (`?x=1&x=2`) and what the server does with that is anyone's
         guess. Either way the report looks like it is sending what you configured.

         Flagged on the LATER row - the first occurrence is the one the user most
         likely meant to keep. Exact match only: `FromDate` and `fromdate` are
         genuinely different keys to both JS and most APIs. */
      if (hasKey) {
        const key = toText(pair.key).trim();
        const firstIndex = pairs.findIndex((p) => toText(p.key).trim() === key);
        if (firstIndex !== -1 && firstIndex !== index) {
          return { field: "key", message: "This key is already used above" };
        }
      }

      // Special handling for date type
      if (pair.dataType === "date") {
        // If there's a key, dateConfig should exist
        if (hasKey && !pair.dateConfig) {
          return { field: "value", message: "Date configuration is required" };
        }
        // If there's dateConfig but no key, key is required
        if (!hasKey && pair.dateConfig) {
          return { field: "key", message: "Key is required" };
        }
        // If both key and dateConfig exist, check if fixedDate is required and missing
        if (hasKey && pair.dateConfig) {
          if (
            pair.dateConfig.dateOption === "fixedDate" &&
            (!pair.dateConfig.fixedDate || pair.dateConfig.fixedDate === "")
          ) {
            return { field: "value", message: "Date is required" };
          }
          return { field: null, message: "" };
        }
        // Empty date row is fine
        return { field: null, message: "" };
      }

      // For non-date types: normal validation
      if (hasKey && !hasValue) {
        return { field: "value", message: "Value is required" };
      }

      if (!hasKey && hasValue) {
        return { field: "key", message: "Key is required" };
      }

      return { field: null, message: "" };
    });
  };

  useEffect(() => {
    if (resourceNameRef.current && cursorPosition !== null) {
      resourceNameRef.current.input.setSelectionRange(cursorPosition, cursorPosition);
      resourceNameRef.current.focus();
    }
  }, [resourceNameValidation]);

  const authOptions = [
    { id: 0, name: "None", value: "none" },
    { id: 1, name: "Basic Auth", value: "basic" },
    { id: 2, name: "JWT", value: "jwt" },
    { id: 3, name: "Auth0", value: "auth0" },
    { id: 4, name: "Cookie", value: "cookie" },
  ];

  const validateUrl = (url) => {
    if (!url.trim()) return false;
    try {
      const urlObj = new URL(url);
      return ["http:", "https:"].includes(urlObj.protocol);
    } catch {
      return false;
    }
  };

  console.log("prps.selectedResource", props);

  // Function to check if current values differ from initial values
  const checkForChanges = (currentValues) => {
    if (!isEditMode) {
      setHasChanges(false);
      return;
    }

    const changed =
      currentValues.apiName !== initialValues.apiName ||
      currentValues.apiUrl !== initialValues.apiUrl ||
      currentValues.httpMethod !== initialValues.httpMethod ||
      currentValues.authMethod !== initialValues.authMethod ||
      currentValues.isAuthRequired !== initialValues.isAuthRequired ||
      currentValues.isWitmegApi !== initialValues.isWitmegApi ||
      currentValues.visibleTo !== initialValues.visibleTo ||
      currentValues.isShareable !== initialValues.isShareable ||
      JSON.stringify(currentValues.urlParams) !== JSON.stringify(initialValues.urlParams) ||
      JSON.stringify(currentValues.headers) !== JSON.stringify(initialValues.headers) ||
      JSON.stringify(currentValues.bodyParams) !== JSON.stringify(initialValues.bodyParams) ||
      JSON.stringify(currentValues.authParams) !== JSON.stringify(initialValues.authParams) ||
      JSON.stringify(currentValues.userIdList) !== JSON.stringify(initialValues.userIdList);

    setHasChanges(changed);
  };

  useEffect(() => {
    if (props.selectedResource && props.selectedResource.connectionData) {
      const resource = props.selectedResource;
      const connectionData = resource.connectionData;

      setIsEditMode(true);

      // Set all state values
      const name = resource.name || "";
      const url = connectionData.baseUrl || "";
      const method = connectionData.requestValues?.httpMethod || "get";
      const auth = connectionData.authInfo?.method || "none";
      const authParms = connectionData.authInfo?.parameters || null;
      const authReq = resource.isAuthRequired || false;
      const witmegApi = resource.isWitmegApi !== undefined ? resource.isWitmegApi : true;
      const visible = resource.visibleTo || "global";
      const shareable = resource.isShareable || false;
      const userIds = resource.sharedWith || [];

      setApiName(name);
      setApiUrl(url);
      setHttpMethod(method);
      setauthMethod(auth);
      setAuthParams(authParms);
      setIsAuthRequired(authReq);
      setIsWitmegApi(witmegApi);
      setVisibleTo(visible);
      setIsShareable(shareable);
      setUserIdList(userIds);

      // Set URL parameters
      let urlParms = [{ key: "", value: "", dataType: "string" }];
      if (connectionData.requestValues?.urlParams && connectionData.requestValues.urlParams.length > 0) {
        urlParms = props.isViewOnly
          ? withoutEmptyPairs(connectionData.requestValues.urlParams)
          : [...connectionData.requestValues.urlParams, { key: "", value: "", dataType: "string" }];
      } else if (props.isViewOnly) {
        urlParms = [];
      }

      setUrlParams(urlParms);

      // Set headers
      let headersList = [{ key: "", value: "", dataType: "string" }];
      if (connectionData.requestValues?.requestHeaders && connectionData.requestValues.requestHeaders.length > 0) {
        headersList = props.isViewOnly
          ? withoutEmptyPairs(connectionData.requestValues.requestHeaders)
          : [...connectionData.requestValues.requestHeaders, { key: "", value: "", dataType: "string" }];
      } else if (props.isViewOnly) {
        headersList = [];
      }

      urlParms = [];

      setHeaders(headersList);

      // Set body parameters
      let bodyParms = [{ key: "", value: "", dataType: "string" }];
      if (connectionData.requestValues?.bodyParams && connectionData.requestValues.bodyParams.length > 0) {
        bodyParms = props.isViewOnly
          ? withoutEmptyPairs(connectionData.requestValues.bodyParams)
          : [...connectionData.requestValues.bodyParams, { key: "", value: "", dataType: "string" }];
      } else if (props.isViewOnly) {
        bodyParms = [];
      }
      setBodyParams(bodyParms);

      // Store initial values for comparison
      const initial = {
        apiName: name,
        apiUrl: url,
        httpMethod: method,
        authMethod: auth,
        authParams: authParms,
        isAuthRequired: authReq,
        isWitmegApi: witmegApi,
        visibleTo: visible,
        isShareable: shareable,
        userIdList: userIds,
        urlParams: urlParms,
        headers: headersList,
        bodyParams: bodyParms,
      };
      setInitialValues(initial);

      // Set form field values
      form.setFieldsValue({
        apinameName: name,
        baseURL: url,
        httpMethodName: method,
        isAuthUserRequired: authReq,
        isWitmegApi: witmegApi,
        visibleTo: visible,
      });
    } else {
      setIsEditMode(false);
      setHasChanges(false);
      setFormSubmitted(false);
      // Reset form for create mode
      form.setFieldsValue({
        apinameName: "",
        baseURL: "",
        httpMethodName: "get",
        isAuthUserRequired: false,
        isWitmegApi: true,
        visibleTo: "global",
      });
    }
  }, [props.selectedResource, form]);

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const addUrlParam = () => {
    const newParams = [...urlParams, { key: "", value: "", dataType: "string" }];
    setUrlParams(newParams);
    setUrlParamsErrors([...urlParamsErrors, ""]);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams: newParams,
      headers,
      bodyParams,
    });
  };

  const removeUrlParam = (index) => {
    const params = [...urlParams];
    const newParams = params.filter((p, i) => index !== i);
    const newErrors = urlParamsErrors.filter((e, i) => index !== i);
    setUrlParams(newParams);
    setUrlParamsErrors(newErrors);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams: newParams,
      headers,
      bodyParams,
    });
  };

  const onChangeUrlParamKey = (event, index, key) => {
    const params = urlParams.map((h, i) => {
      return {
        key: i === index && key === "key" ? event.target.value : h.key,
        value:
          i === index && key === "value"
            ? event.target.value.trim() === ""
              ? ""
              : Number(event.target.value)
                ? Number(event.target.value)
                : event.target.value === "0"
                  ? 0
                  : event.target.value
            : h.value,
        dataType: i === index && key === "dataType" ? event : h.dataType,
        dateConfig: h.dateConfig
          ? h.dateConfig
          : i === index && key === "dataType" && event === "date"
            ? {
                dateOption: "daysBefore",
                timeUnits: 1,
                fixedDate: null,
                dateFormat: "YYYY-MM-DDTHH:mm:ss.SS[Z]",
              }
            : null,
      };
    });
    setUrlParams(params);
    // Validate on change
    if (formSubmitted) {
      const keyValueErrors = validateKeyValuePairs(params);
      const typeErrors = validateDataTypes(params);
      const finalErrors = keyValueErrors.map((error, idx) => {
        if (error.field) return error;
        return typeErrors[idx];
      });
      setUrlParamsErrors(finalErrors);
    }

    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams: params,
      headers,
      bodyParams,
    });
  };

  const addHeader = () => {
    const newHeaders = [...headers, { key: "", value: "", dataType: "string" }];
    setHeaders(newHeaders);
    setHeadersErrors([...headersErrors, ""]);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams,
      headers: newHeaders,
      bodyParams,
    });
  };

  const removeHeader = (index) => {
    const headersList = [...headers];
    const newHeaders = headersList.filter((p, i) => index !== i);
    const newErrors = headersErrors.filter((e, i) => index !== i);
    setHeaders(newHeaders);
    setHeadersErrors(newErrors);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams,
      headers: newHeaders,
      bodyParams,
    });
  };

  const onChangeHeaderKey = (event, index, key) => {
    const headersList = headers.map((h, i) => {
      return {
        key: i === index && key === "key" ? event.target.value : h.key,
        value:
          i === index && key === "value"
            ? event.target.value.trim() === ""
              ? ""
              : Number(event.target.value)
                ? Number(event.target.value)
                : event.target.value === "0"
                  ? 0
                  : event.target.value
            : h.value,
        dataType: i === index && key === "dataType" ? event : h.dataType,
        dateConfig: h.dateConfig
          ? h.dateConfig
          : i === index && key === "dataType" && event === "date"
            ? {
                dateOption: "daysBefore",
                timeUnits: 1,
                fixedDate: null,
                dateFormat: "YYYY-MM-DDTHH:mm:ss.SS[Z]",
              }
            : null,
      };
    });
    setHeaders(headersList);
    // Validate on change
    if (formSubmitted) {
      const keyValueErrors = validateKeyValuePairs(headersList);
      const typeErrors = validateDataTypes(headersList);
      const finalErrors = keyValueErrors.map((error, idx) => {
        if (error.field) return error;
        return typeErrors[idx];
      });
      setHeadersErrors(finalErrors);
    }

    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams,
      headers: headersList,
      bodyParams,
    });
  };

  const addBodyParams = () => {
    const newBodyParams = [...bodyParams, { key: "", value: "", dataType: "string" }];
    setBodyParams(newBodyParams);
    setBodyParamsErrors([...bodyParamsErrors, ""]);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams,
      headers,
      bodyParams: newBodyParams,
    });
  };

  const removeBodyParams = (index) => {
    const headersList = [...bodyParams];
    const newBodyParams = headersList.filter((p, i) => index !== i);
    const newErrors = bodyParamsErrors.filter((e, i) => index !== i);
    setBodyParams(newBodyParams);
    setBodyParamsErrors(newErrors);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams,
      headers,
      bodyParams: newBodyParams,
    });
  };

  const onChangeBodyParamsKey = (event, index, key) => {
    let bodyParamsList = bodyParams.map((bp, i) => {
      return {
        key: i === index && key === "key" ? event.target.value : bp.key,
        value:
          i === index && key === "value"
            ? event.target.value.trim() === ""
              ? ""
              : Number(event.target.value)
                ? Number(event.target.value)
                : event.target.value === "0"
                  ? 0
                  : event.target.value
            : bp.value,
        dataType: i === index && key === "dataType" ? event : bp.dataType,
        dateConfig: bp.dateConfig
          ? bp.dateConfig
          : i === index && key === "dataType" && event === "date"
            ? {
                dateOption: "daysBefore",
                timeUnits: 1,
                fixedDate: null,
                dateFormat: "YYYY-MM-DDTHH:mm:ss.SS[Z]",
              }
            : null,
      };
    });
    setBodyParams(bodyParamsList);

    if (formSubmitted) {
      const keyValueErrors = validateKeyValuePairs(bodyParamsList);
      const typeErrors = validateDataTypes(bodyParamsList);
      const finalErrors = keyValueErrors.map((error, idx) => {
        if (error.field) return error;
        return typeErrors[idx];
      });
      setBodyParamsErrors(finalErrors);
    }

    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList,
      urlParams,
      headers,
      bodyParams: bodyParamsList,
    });
  };

  const testRestApi = async () => {
    setApiTestStatus("pending");
    setApiSaveStatus("");
    if (apiName === "") {
      setApiNameError(true);
    }
    console.log("apiUrl", apiUrl);

    let urlParamSring = "";
    if (urlParams.length > 0) {
      urlParamSring = urlParamSring + "?";
      for (let i = 0; i < urlParams.length; i++) {
        if (urlParams[i]["key"] !== "" && urlParams[i]["value"] !== "") {
          urlParamSring = urlParamSring + urlParams[i]["key"] + "=" + urlParams[i]["value"] + "&";
        }
      }
      urlParamSring.slice(0, -1);
    }

    let headerObj = {};
    if (headers.length > 0) {
      for (let i = 0; i < headers.length; i++) {
        if (headers[i]["key"] !== "" && headers[i]["value"] !== "") {
          headerObj[headers[i]["key"]] = headers[i]["value"];
        }
      }
    }
    let bodyParamObj = {};

    if (bodyParams.length > 0) {
      for (let i = 0; i < bodyParams.length; i++) {
        if (bodyParams[i]["key"] !== "" && bodyParams[i]["value"] !== "") {
          bodyParamObj[bodyParams[i]["key"]] = bodyParams[i]["value"];
        }
      }
    }

    let sendUrl = apiUrl + urlParamSring;

    if (authParams) {
      console.log("has  authParams");
      if (authMethod === "jwt") {
        console.log("JWT PROCESS");
        const timestamp = Math.floor(Date.now() / 1000) + 1000;
        const privateKey = authParams.privateKey;
        var payload = {
          aud: authParams.aud,
          exp: timestamp,
          iss: authParams.iss,
        };
        var signOptions = {
          algorithm: "RS256",
        };

        var privateKeyVal = privateKey ? privateKey.replace(/\\n/g, "\n") : null;

        if (privateKeyVal) {
          try {
            var token = jwt.sign(payload, privateKeyVal, signOptions);
            console.log("Generated JWT authParams=====>>1", token);
            setApiTestStatus("success");
          } catch (e) {
            console.log("JWT authPsign error=====>>", e);
            setApiTestStatus("error");
          }
        }
      } else {
        bodyParamObj = Object.assign({}, authParams);
        console.log("bodyParamObj=====>>2", bodyParamObj);
        try {
          setApiTestStatus("success");
        } catch (error) {
          setApiTestStatus("error");
          console.log(error);
        }
      }
    } else {
      let config = {
        method: httpMethod,
        url: sendUrl,
      };
      if (httpMethod === "post") {
        config.data = bodyParamObj;
      }
      if (headers.length > 0) {
        config.headers = headerObj;
      } else {
        config.headers = {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
        };
      }
      console.log("bodyParamObj=====>>2", config);
      try {
        setApiTestStatus("success");
      } catch (error) {
        setApiTestStatus("error");
        console.log(error);
      }
    }
  };
  console.log("propsss", props);

  // REACT_APP_CONNECTION_INFO_BASE_URL
  const checkGroupNameDuplicate = async (name) => {
    if (!name || name.trim() === "") {
      setResourceNameValidation({ status: "", message: "" });
      return;
    }

    // Store cursor position BEFORE setting checking status
    if (resourceNameRef.current && resourceNameRef.current.input) {
      setCursorPosition(resourceNameRef.current.input.selectionStart);
    }

    setResourceNameValidation({ status: "checking", message: "Checking availability..." });
    try {
      const response = await fetch(`${REACT_APP_CONNECTION_INFO_BASE_URL}/check-resource-duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          id: props.selectedResource?.id || null, // For edit mode
        }),
      });

      const data = await response.json();

      // Update cursor position again before setting result
      if (resourceNameRef.current && resourceNameRef.current.input) {
        setCursorPosition(resourceNameRef.current.input.selectionStart);
      }

      if (data.isDuplicate) {
        setResourceNameValidation({
          status: "duplicate",
          message: data.message || "A Resource with this name already exists",
        });
      } else {
        setResourceNameValidation({
          status: "available",
          message: "Resource name is available",
        });
      }
    } catch (error) {
      setResourceNameValidation({
        status: "error",
        message: "Error checking resource name availability",
      });
    }
  };

  const handleSaveRestApi = async () => {
    const urlParamsValidation = validateKeyValuePairs(urlParams);
    const headersValidation = validateKeyValuePairs(headers);
    const bodyParamsValidation = validateKeyValuePairs(bodyParams);

    const urlParamsTypeValidation = validateDataTypes(urlParams);
    const headersTypeValidation = validateDataTypes(headers);
    const bodyParamsTypeValidation = validateDataTypes(bodyParams);

    // Merge validations
    const finalUrlParamsErrors = urlParamsValidation.map((error, index) => {
      if (error.field) return error;
      return urlParamsTypeValidation[index];
    });

    const finalHeadersErrors = headersValidation.map((error, index) => {
      if (error.field) return error;
      return headersTypeValidation[index];
    });

    const finalBodyParamsErrors = bodyParamsValidation.map((error, index) => {
      if (error.field) return error;
      return bodyParamsTypeValidation[index];
    });

    // Set all error states
    setUrlParamsErrors(finalUrlParamsErrors);
    setHeadersErrors(finalHeadersErrors);
    setBodyParamsErrors(finalBodyParamsErrors);

    // Collect ALL validation errors
    let hasErrors = false;

    // Check key-value pair errors
    const hasKeyValueErrors =
      finalUrlParamsErrors.some((e) => e.field) ||
      finalHeadersErrors.some((e) => e.field) ||
      finalBodyParamsErrors.some((e) => e.field);

    if (hasKeyValueErrors) {
      console.log("hasKeyValueErrors");

      hasErrors = true;
    }

    // Check API name
    if (apiName === "" || apiName.trim() === "") {
      console.log("apiName errors");

      hasErrors = true;
      setApiNameError(true);
    } else {
      setApiNameError(false);
    }

    // Check API URL
    if (apiUrl === "" || !validateUrl(apiUrl)) {
      console.log("api url  errors");
      hasErrors = true;
      setApiUrlError(true);
    } else {
      setApiUrlError(false);
    }

    // Check authentication form validity
    if (authMethod !== "none" && !authFormValid) {
      hasErrors = true;

      setApiSaveStatus("error");
    }

    // Check resource name duplication
    if (resourceNameValidation.status === "duplicate") {
      console.log("resourceName duplicate  errors");

      hasErrors = true;
    }

    // If any errors exist, prevent submission
    if (hasErrors) {
      setFormSubmitted(true);
      setApiSaveStatus("error");
      return;
    }

    //  All validations passed - proceed with save
    const API_INFO_URL = props.selectedResource
      ? `${REACT_APP_CONNECTION_INFO_BASE_URL}/update-resource`
      : `${REACT_APP_CONNECTION_INFO_BASE_URL}/resource`;
    const JWT_TOKEN = `Bearer ${localStorage.getItem("accessToken")}`;
    setApiTestStatus("");
    setApiSaveStatus("pending");

    if (apiName.trim() !== "" && apiUrl !== "" && httpMethod) {
      let postData = {
        name: apiName,
        icon: props.resourceType.icon,
        category: props.resourceType.category,
        description: props.resourceType.description,
        isActive: true,
        isAuthRequired: isAuthRequired,
        isWitmegApi: isWitmegApi,
        resoueceType: "Rest API",
        userId: localStorage.getItem("userID"),
        appId: !props.appsMultiSelectEnabled ? props.appList[0] : null,
        appIdList: props.appsMultiSelectEnabled ? props.appList : null,
        isShareable: isShareable,
        sharedWith: isShareable ? userIdList : null,
        connectionData: {
          name: apiName,
          baseUrl: apiUrl,
          authInfo: {
            method: authMethod,
            parameters: authParams ? authParams : {},
          },
          requestValues: {
            httpMethod: httpMethod,
            urlParams: urlParams.filter((qp) => toText(qp.key).trim() !== ""),
            requestHeaders: headers.filter((hp) => toText(hp.key).trim() !== ""),
            bodyParams: bodyParams.filter((bp) => toText(bp.key).trim() !== ""),
          },
        },
      };
      if (isWitmegApi) {
        postData.visibleTo = visibleTo;
      }
      if (props.selectedResource) {
        postData.id = props.selectedResource.id;
      }

      let reqHeaders = {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        Authorization: JWT_TOKEN,
      };

      console.clear();

      try {
        const response = await Axios.post(API_INFO_URL, postData, { headers: reqHeaders });
        console.log(response.data);

        setApiSaveStatus("success");
        if (props.clearSearch) {
          props.clearSearch();
        }

        dispatch(getResourcesAsync());
        setTimeout(() => {
          props.handleClose();
        }, 1500);
      } catch (error) {
        setApiSaveStatus("error");

        console.log("restapierror", error);
      }
    }
  };

  const onSelectSharableUsers = (userObj) => {
    console.log("onSelectSharableUsers===>>", userObj);
    let users = [...userIdList];
    users.push(userObj.data.UserDetails.UserID);
    const newUserIdList = [...new Set(users)];
    setUserIdList(newUserIdList);
    checkForChanges({
      apiName,
      apiUrl,
      httpMethod,
      authMethod,
      authParams,
      isAuthRequired,
      isWitmegApi,
      visibleTo,
      isShareable,
      userIdList: newUserIdList,
      urlParams,
      headers,
      bodyParams,
    });
  };

  const handleSetDateOptions = (index, dateConfig, paramType) => {
    console.log("dateConfig===>>", dateConfig);
    console.log("index===>>", index);
    console.log("paramType===>>", paramType);

    if (paramType === "bodyParams") {
      let bodyParamsList = bodyParams.map((bp, i) => {
        return {
          ...bp,
          dateConfig: i === index ? dateConfig : bp.dateConfig,
        };
      });
      console.log("bodyParamsList ====>>>", bodyParamsList);
      setBodyParams(bodyParamsList);
      checkForChanges({
        apiName,
        apiUrl,
        httpMethod,
        authMethod,
        authParams,
        isAuthRequired,
        isWitmegApi,
        visibleTo,
        isShareable,
        userIdList,
        urlParams,
        headers,
        bodyParams: bodyParamsList,
      });
    } else if (paramType === "urlParams") {
      let urlParamsList = urlParams.map((up, i) => {
        return {
          ...up,
          dateConfig: i === index ? dateConfig : up.dateConfig,
        };
      });
      console.log("urlParamsList ====>>>", urlParamsList);
      setUrlParams(urlParamsList);
      checkForChanges({
        apiName,
        apiUrl,
        httpMethod,
        authMethod,
        authParams,
        isAuthRequired,
        isWitmegApi,
        visibleTo,
        isShareable,
        userIdList,
        urlParams: urlParamsList,
        headers,
        bodyParams,
      });
    } else if (paramType === "headers") {
      let headersList = headers.map((h, i) => {
        return {
          ...h,
          dateConfig: i === index ? dateConfig : h.dateConfig,
        };
      });
      console.log("headersList ====>>>", headersList);
      setHeaders(headersList);
      checkForChanges({
        apiName,
        apiUrl,
        httpMethod,
        authMethod,
        authParams,
        isAuthRequired,
        isWitmegApi,
        visibleTo,
        isShareable,
        userIdList,
        urlParams,
        headers: headersList,
        bodyParams,
      });
    }
  };

  const handleClearDateError = (index, paramType) => {
    if (paramType === "urlParams") {
      const newErrors = [...urlParamsErrors];
      if (newErrors[index]?.field === "value") {
        newErrors[index] = { field: null, message: "" };
        setUrlParamsErrors(newErrors);
      }
    } else if (paramType === "headers") {
      const newErrors = [...headersErrors];
      if (newErrors[index]?.field === "value") {
        newErrors[index] = { field: null, message: "" };
        setHeadersErrors(newErrors);
      }
    } else if (paramType === "bodyParams") {
      const newErrors = [...bodyParamsErrors];
      if (newErrors[index]?.field === "value") {
        newErrors[index] = { field: null, message: "" };
        setBodyParamsErrors(newErrors);
      }
    }
  };

  return (
    <div>
      <Form
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        name="basic"
        form={form}
        initialValues={{
          visibleTo: visibleTo,
          httpMethodName: "get",
          isAuthUserRequired: false,
          isWitmegApi: true,
        }}
        onFinish={handleSaveRestApi}
        onFinishFailed={onFinishFailed}
      >
        <div style={{ height: "50vh", overflowY: "auto" }}>
          <Form.Item
            style={{ marginBottom: "8px" }}
            label="Name"
            name="apinameName"
            validateStatus={
              apiName.length > 0 && apiName.length < 2
                ? "error"
                : resourceNameValidation.status === "duplicate" || (formSubmitted && apiName === "")
                  ? "error"
                  : resourceNameValidation.status === "available"
                    ? "success"
                    : ""
            }
            help={
              apiName.length > 0 && apiName.length < 2 ? (
                "Name must be at least 2 characters long!"
              ) : resourceNameValidation.status === "duplicate" ? (
                resourceNameValidation.message
              ) : resourceNameValidation.status === "available" ? (
                <span style={{ color: "#52c41a" }}>{resourceNameValidation.message}</span>
              ) : formSubmitted && apiName === "" ? (
                "Please input name"
              ) : (
                ""
              )
            }
            rules={[{ required: true, message: "Please input API name!" }]}
          >
            <Input
              value={apiName}
              ref={resourceNameRef}
              onChange={(e) => {
                const newName = e.target.value;

                const currentCursor = e.target.selectionStart;
                setCursorPosition(currentCursor);
                setApiName(newName);
                setApiNameError(false);

                if (debounceTimer) {
                  clearTimeout(debounceTimer);
                }

                // Set new timer for debounced API call
                if (newName.trim().length >= 2) {
                  const newTimer = setTimeout(() => {
                    checkGroupNameDuplicate(newName);
                  }, 800);

                  setDebounceTimer(newTimer);
                }

                checkForChanges({
                  apiName: newName,
                  apiUrl,
                  httpMethod,
                  authMethod,
                  authParams,
                  isAuthRequired,
                  isWitmegApi,
                  visibleTo,
                  isShareable,
                  userIdList,
                  urlParams,
                  headers,
                  bodyParams,
                });
              }}
              disabled={props.isViewOnly}
              onKeyDown={(e) => {
                // Save cursor position on every keypress
                setCursorPosition(e.target.selectionStart);
              }}
              onClick={(e) => {
                // Save cursor position on click
                setCursorPosition(e.target.selectionStart);
              }}
              suffix={
                resourceNameValidation.status === "checking" ? (
                  <Spin size="small" />
                ) : resourceNameValidation.status === "available" ? (
                  <span style={{ color: "#52c41a", fontSize: "16px" }}>✓</span>
                ) : resourceNameValidation.status === "duplicate" ? (
                  <span style={{ color: "#ff4d4f", fontSize: "16px" }}>⚠</span>
                ) : null
              }
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: "8px" }} label="Auth User Required" name="isAuthUserRequired">
            <Switch
              checked={isAuthRequired}
              onChange={(checked) => {
                setIsAuthRequired(checked);
                checkForChanges({
                  apiName,
                  apiUrl,
                  httpMethod,
                  authMethod,
                  authParams,
                  isAuthRequired: checked,
                  isWitmegApi,
                  visibleTo,
                  isShareable,
                  userIdList,
                  urlParams,
                  headers,
                  bodyParams,
                });
              }}
              disabled={props.isViewOnly}
            />
          </Form.Item>

          {localStorage.getItem("IsSpecialUser") === "true" ? (
            <>
              <Form.Item style={{ marginBottom: "8px" }} label="Witmeg API" name="isWitmegApi">
                <Switch
                  checked={isWitmegApi}
                  onChange={(checked) => {
                    setIsWitmegApi(checked);
                    checkForChanges({
                      apiName,
                      apiUrl,
                      httpMethod,
                      authMethod,
                      authParams,
                      isAuthRequired,
                      isWitmegApi: checked,
                      visibleTo,
                      isShareable,
                      userIdList,
                      urlParams,
                      headers,
                      bodyParams,
                    });
                  }}
                  disabled={props.isViewOnly}
                />
              </Form.Item>

              {isWitmegApi ? (
                <>
                  <Form.Item style={{ marginBottom: "8px" }} label="Visible To" name="visibleTo">
                    <Select
                      style={{ width: "30%" }}
                      value={visibleTo}
                      placeholder="Visible To"
                      // getPopupContainer={() => document.getElementById("rest-api-modal-content")}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      onChange={(e) => {
                        setVisibleTo(e);
                        checkForChanges({
                          apiName,
                          apiUrl,
                          httpMethod,
                          authMethod,
                          authParams,
                          isAuthRequired,
                          isWitmegApi,
                          visibleTo: e,
                          isShareable,
                          userIdList,
                          urlParams,
                          headers,
                          bodyParams,
                        });
                      }}
                      disabled={props.isViewOnly}
                    >
                      <Select.Option value="global">Global</Select.Option>
                      <Select.Option value="internal">Internal</Select.Option>
                    </Select>
                  </Form.Item>
                </>
              ) : null}
            </>
          ) : null}

          <Divider />

          <Form.Item
            style={{ marginBottom: "8px" }}
            label="Base URL"
            name="baseURL"
            validateStatus={apiUrlError ? "error" : ""}
            rules={[{ required: true, message: "Please input API URL" }]}
            help={apiUrlError ? "Please enter a valid URL (must start with http:// or https://)" : ""}
          >
            <Input
              value={apiUrl}
              onChange={(e) => {
                const newUrl = e.target.value;
                setApiUrl(newUrl);
                setApiUrlError(false);
                checkForChanges({
                  apiName,
                  apiUrl: newUrl,
                  httpMethod,
                  authMethod,
                  authParams,
                  isAuthRequired,
                  isWitmegApi,
                  visibleTo,
                  isShareable,
                  userIdList,
                  urlParams,
                  headers,
                  bodyParams,
                });
              }}
              disabled={props.isViewOnly}
            />
          </Form.Item>

          <div style={{ marginTop: "16px" }}>
            <div style={{ marginTop: "16px" }}>
              {urlParams.length > 0 &&
                urlParams.map((urlParam, index) => (
                  <Row key={"url_param_" + index} gutter={2} style={{ marginBottom: "8px" }}>
                    <Col span={6} style={{ textAlign: "right", paddingRight: "8px" }}>
                      {index === 0 ? <span>URL Parameterss</span> : ""}
                    </Col>
                    <Col span={18}>
                      <div id="rest-api-modal-content">
                        <Input.Group compact style={{ display: "flex", gap: "5px" }}>
                          <div style={{ width: "30%", display: "inline-block" }}>
                            <Input
                              style={{
                                width: "100%",
                                marginLeft: "-5px",
                                borderColor: urlParamsErrors[index]?.field === "key" ? "#ff4d4f" : undefined,
                              }}
                              placeholder="Key"
                              value={urlParams[index].key}
                              onChange={(e) => {
                                onChangeUrlParamKey(e, index, "key");
                              }}
                              disabled={props.isViewOnly}
                            />
                            {urlParamsErrors[index]?.field === "key" && (
                              <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                                {urlParamsErrors[index].message}
                              </div>
                            )}
                          </div>

                          <Select
                            style={{ width: "30%" }}
                            placeholder="Data Type"
                            value={urlParams[index].dataType}
                            onChange={(e) => {
                              onChangeUrlParamKey(e, index, "dataType");
                            }}
                            disabled={props.isViewOnly}
                            getPopupContainer={(trigger) => trigger.parentNode}
                          >
                            <Select.Option value="number">Number</Select.Option>
                            <Select.Option value="string">String</Select.Option>
                            <Select.Option value="boolean">Boolean</Select.Option>
                            <Select.Option value="date">Date</Select.Option>
                          </Select>

                          <div style={{ width: "40%", display: "inline-block" }}>
                            {urlParams[index].dataType !== "date" ? (
                              <>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <Input
                                    style={{
                                      width: "100%",
                                      borderColor: urlParamsErrors[index]?.field === "value" ? "#ff4d4f" : undefined,
                                    }}
                                    placeholder="Value"
                                    value={urlParams[index].value}
                                    onChange={(e) => {
                                      onChangeUrlParamKey(e, index, "value");
                                    }}
                                    disabled={props.isViewOnly}
                                  />
                                  {!props.isViewOnly && (
                                    <span
                                      style={{
                                        color: "#333",
                                        cursor: "pointer",
                                        display: "flex",
                                        marginLeft: "5px",
                                        alignItems: "center",
                                        background: "#f0f0f0",
                                        padding: "8px 10px",
                                        borderRadius: "0 2px 2px 0",
                                        border: "1px solid #d9d9d9",
                                      }}
                                      onClick={() => removeUrlParam(index)}
                                    >
                                      <CloseOutlined />
                                    </span>
                                  )}
                                </div>
                                {urlParamsErrors[index]?.field === "value" && (
                                  <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                                    {urlParamsErrors[index].message}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <Input style={{ width: "100%" }} disabled />
                                {!props.isViewOnly && (
                                  <span
                                    style={{
                                      color: "#333",
                                      cursor: "pointer",
                                      display: "flex",
                                      marginLeft: "5px",
                                      alignItems: "center",
                                      background: "#f0f0f0",
                                      padding: "8px 10px",
                                      borderRadius: "0 2px 2px 0",
                                      border: "1px solid #d9d9d9",
                                    }}
                                    onClick={() => removeUrlParam(index)}
                                  >
                                    <CloseOutlined />
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </Input.Group>

                        {urlParams[index].dataType === "date" && (
                          <DateConfigSelect
                            handleSetDateOptions={(data) => handleSetDateOptions(index, data, "urlParams")}
                            dateConfig={urlParam.dateConfig}
                            isViewOnly={props.isViewOnly}
                            showError={formSubmitted && urlParamsErrors[index]?.field === "value"}
                            errorMessage={urlParamsErrors[index]?.message}
                            onClearError={() => handleClearDateError(index, "urlParams")}
                          />
                        )}

                        {index === urlParams.length - 1 && !props.isViewOnly && (
                          <Button
                            type="dashed"
                            onClick={() => addUrlParam()}
                            block
                            icon={<PlusOutlined />}
                            style={{ marginTop: "8px" }}
                          >
                            Add new
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                ))}
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <div style={{ marginTop: "16px" }}>
              {headers.map((header, index) => (
                <Row key={"header_" + index} gutter={2} style={{ marginBottom: "8px" }}>
                  <Col span={6} style={{ textAlign: "right", paddingRight: "8px" }}>
                    {index === 0 ? <span>Headers</span> : ""}
                  </Col>
                  <Col span={18}>
                    <div>
                      <Input.Group compact style={{ display: "flex", gap: "5px" }}>
                        <div style={{ width: "30%", display: "inline-block" }}>
                          <Input
                            style={{
                              width: "100%",
                              marginLeft: "-5px",
                              borderColor: headersErrors[index]?.field === "key" ? "#ff4d4f" : undefined,
                            }}
                            placeholder="Key"
                            value={headers[index].key}
                            onChange={(e) => {
                              onChangeHeaderKey(e, index, "key");
                            }}
                            disabled={props.isViewOnly}
                          />
                          {headersErrors[index]?.field === "key" && (
                            <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                              {headersErrors[index].message}
                            </div>
                          )}
                        </div>

                        <Select
                          style={{ width: "30%" }}
                          placeholder="Data Type"
                          value={headers[index].dataType}
                          onChange={(e) => {
                            onChangeHeaderKey(e, index, "dataType");
                          }}
                          getPopupContainer={(trigger) => trigger.parentNode}
                          disabled={props.isViewOnly}
                        >
                          <Select.Option value="number">Number</Select.Option>
                          <Select.Option value="string">String</Select.Option>
                          <Select.Option value="boolean">Boolean</Select.Option>
                          <Select.Option value="date">Date</Select.Option>
                        </Select>

                        <div style={{ width: "40%", display: "inline-block" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Input
                              style={{
                                width: "100%",
                                borderColor: headersErrors[index]?.field === "value" ? "#ff4d4f" : undefined,
                              }}
                              placeholder="Value"
                              value={headers[index].value}
                              onChange={(e) => {
                                onChangeHeaderKey(e, index, "value");
                              }}
                              disabled={props.isViewOnly}
                            />
                            {!props.isViewOnly && (
                              <span
                                style={{
                                  color: "#333",
                                  cursor: "pointer",
                                  display: "flex",
                                  marginLeft: "5px",
                                  alignItems: "center",
                                  background: "#f0f0f0",
                                  padding: "8px 10px",
                                  borderRadius: "0 2px 2px 0",
                                  border: "1px solid #d9d9d9",
                                }}
                                onClick={() => removeHeader(index)}
                              >
                                <CloseOutlined />
                              </span>
                            )}
                          </div>
                          {headersErrors[index]?.field === "value" && (
                            <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                              {headersErrors[index].message}
                            </div>
                          )}
                        </div>
                      </Input.Group>

                      {headers[index].dataType === "date" && (
                        <DateConfigSelect
                          handleSetDateOptions={(data) => handleSetDateOptions(index, data, "headers")}
                          dateConfig={header.dateConfig}
                          isViewOnly={props.isViewOnly}
                          showError={formSubmitted && headersErrors[index]?.field === "value"}
                          errorMessage={headersErrors[index]?.message}
                          onClearError={() => handleClearDateError(index, "headers")}
                        />
                      )}

                      {index === headers.length - 1 && !props.isViewOnly && (
                        <Button
                          type="dashed"
                          onClick={() => addHeader()}
                          block
                          icon={<PlusOutlined />}
                          style={{ marginTop: "8px" }}
                        >
                          Add new
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              ))}
            </div>
          </div>

          <div style={{ marginTop: "16px" }}>
            <Form.Item
              style={{ marginBottom: "8px" }}
              label="HTTP Method"
              name="httpMethodName"
              rules={[{ required: true, message: "Please select HTTP Method!" }]}
            >
              <Select
                onChange={(e) => {
                  setHttpMethod(e);
                  checkForChanges({
                    apiName,
                    apiUrl,
                    httpMethod: e,
                    authMethod,
                    authParams,
                    isAuthRequired,
                    isWitmegApi,
                    visibleTo,
                    isShareable,
                    userIdList,
                    urlParams,
                    headers,
                    bodyParams,
                  });
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownAlign={{ points: ["tl", "bl"], offset: [2, 4], overflow: { adjustX: 50, adjustY: 10 } }}
                dropdownMatchSelectWidth={false}
                value={httpMethod}
                disabled={props.isViewOnly}
              >
                <Select.Option value="get">GET</Select.Option>
                <Select.Option value="post">POST</Select.Option>
              </Select>
            </Form.Item>
          </div>

          {httpMethod === "post" ? (
            <div style={{ marginTop: "16px" }}>
              {httpMethod === "post" ? (
                <div style={{ marginTop: "16px" }}>
                  {bodyParams.map((bp, index) => (
                    <Row key={"bodyParam_" + index} gutter={2} style={{ marginBottom: "8px" }}>
                      <Col span={6} style={{ textAlign: "right", paddingRight: "8px" }}>
                        {index === 0 ? <span>Extra Body Values</span> : ""}
                      </Col>
                      <Col span={18}>
                        <div>
                          <Input.Group compact style={{ display: "flex", gap: "5px" }}>
                            <div style={{ width: "30%", display: "inline-block" }}>
                              <Input
                                style={{
                                  width: "100%",
                                  marginLeft: "-5px",
                                  borderColor: bodyParamsErrors[index]?.field === "key" ? "#ff4d4f" : undefined,
                                }}
                                placeholder="Key"
                                value={bodyParams[index].key}
                                onChange={(e) => {
                                  onChangeBodyParamsKey(e, index, "key");
                                }}
                                disabled={props.isViewOnly}
                              />
                              {bodyParamsErrors[index]?.field === "key" && (
                                <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                                  {bodyParamsErrors[index].message}
                                </div>
                              )}
                            </div>

                            <Select
                              style={{ width: "30%" }}
                              placeholder="Data Type"
                              value={bodyParams[index].dataType}
                              onChange={(e) => {
                                onChangeBodyParamsKey(e, index, "dataType");
                              }}
                              getPopupContainer={(trigger) => trigger.parentNode}
                              disabled={props.isViewOnly}
                            >
                              <Select.Option value="number">Number</Select.Option>
                              <Select.Option value="string">String</Select.Option>
                              <Select.Option value="boolean">Boolean</Select.Option>
                              <Select.Option value="date">Date</Select.Option>
                            </Select>

                            <div style={{ width: "40%", display: "inline-block" }}>
                              {bodyParams[index].dataType !== "date" ? (
                                <>
                                  <div style={{ display: "flex", alignItems: "center" }}>
                                    <Input
                                      style={{
                                        width: "100%",
                                        borderColor: bodyParamsErrors[index]?.field === "value" ? "#ff4d4f" : undefined,
                                      }}
                                      placeholder="Value"
                                      value={bodyParams[index].value}
                                      onChange={(e) => {
                                        onChangeBodyParamsKey(e, index, "value");
                                      }}
                                      disabled={props.isViewOnly}
                                    />
                                    {!props.isViewOnly && (
                                      <span
                                        style={{
                                          color: "#333",
                                          cursor: "pointer",
                                          display: "flex",
                                          marginLeft: "5px",
                                          alignItems: "center",
                                          background: "#f0f0f0",
                                          padding: "8px 10px",
                                          borderRadius: "0 2px 2px 0",
                                          border: "1px solid #d9d9d9",
                                        }}
                                        onClick={() => removeBodyParams(index)}
                                      >
                                        <CloseOutlined />
                                      </span>
                                    )}
                                  </div>
                                  {bodyParamsErrors[index]?.field === "value" && (
                                    <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                                      {bodyParamsErrors[index].message}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <Input style={{ width: "100%" }} disabled />
                                  {!props.isViewOnly && (
                                    <span
                                      style={{
                                        color: "#333",
                                        cursor: "pointer",
                                        display: "flex",
                                        marginLeft: "5px",
                                        alignItems: "center",
                                        background: "#f0f0f0",
                                        padding: "8px 10px",
                                        borderRadius: "0 2px 2px 0",
                                        border: "1px solid #d9d9d9",
                                      }}
                                      onClick={() => removeBodyParams(index)}
                                    >
                                      <CloseOutlined />
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </Input.Group>

                          {bodyParams[index].dataType === "date" && (
                            <DateConfigSelect
                              handleSetDateOptions={(data) => handleSetDateOptions(index, data, "bodyParams")}
                              dateConfig={bp.dateConfig}
                              isViewOnly={props.isViewOnly}
                              showError={formSubmitted && bodyParamsErrors[index]?.field === "value"}
                              errorMessage={bodyParamsErrors[index]?.message}
                              onClearError={() => handleClearDateError(index, "bodyParams")}
                            />
                          )}

                          {index === bodyParams.length - 1 && !props.isViewOnly && (
                            <Button
                              type="dashed"
                              onClick={() => addBodyParams()}
                              block
                              icon={<PlusOutlined />}
                              style={{ marginTop: "8px" }}
                            >
                              Add new
                            </Button>
                          )}
                        </div>
                      </Col>
                    </Row>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div style={{ marginTop: "16px", position: "relative" }}>
            <Form.Item style={{ marginBottom: "8px" }} label="Authentication" validateFirst={authMethod}>
              <Select
                onChange={(e) => {
                  setauthMethod(e);
                  setAuthParams(null);
                  checkForChanges({
                    apiName,
                    apiUrl,
                    httpMethod,
                    authMethod: e,
                    authParams: null,
                    isAuthRequired,
                    isWitmegApi,
                    visibleTo,
                    isShareable,
                    userIdList,
                    urlParams,
                    headers,
                    bodyParams,
                  });
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                dropdownAlign={{ points: ["tl", "bl"], offset: [2, 4], overflow: { adjustX: 50, adjustY: 10 } }}
                dropdownMatchSelectWidth={false}
                value={authMethod}
                disabled={props.isViewOnly}
              >
                {authOptions.map((opt) => {
                  return (
                    <Select.Option key={"auth_opt_" + opt.id} value={opt.value}>
                      {opt.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </div>

          {(() => {
            switch (authMethod) {
              case "basic":
                return (
                  <BasicAuthForm
                    handleChangeValues={(params) => {
                      setAuthParams(params);
                      checkForChanges({
                        apiName,
                        apiUrl,
                        httpMethod,
                        authMethod,
                        authParams: params,
                        isAuthRequired,
                        isWitmegApi,
                        visibleTo,
                        isShareable,
                        userIdList,
                        urlParams,
                        headers,
                        bodyParams,
                      });
                    }}
                    isViewOnly={props.isViewOnly}
                    authData={
                      props.selectedResource &&
                      props.selectedResource.connectionData &&
                      props.selectedResource.connectionData.authInfo &&
                      props.selectedResource.connectionData.authInfo.parameters
                        ? props.selectedResource.connectionData.authInfo.parameters
                        : null
                    }
                    connectionData={props?.selectedResource?.connectionData}
                    onValidationChange={setAuthFormValid}
                  />
                );
              case "auth0":
                return (
                  <AuthOForm
                    handleChangeValues={(params) => {
                      setAuthParams(params);
                      checkForChanges({
                        apiName,
                        apiUrl,
                        httpMethod,
                        authMethod,
                        authParams: params,
                        isAuthRequired,
                        isWitmegApi,
                        visibleTo,
                        isShareable,
                        userIdList,
                        urlParams,
                        headers,
                        bodyParams,
                      });
                    }}
                    isViewOnly={props.isViewOnly}
                    authData={
                      props.selectedResource &&
                      props.selectedResource.connectionData &&
                      props.selectedResource.connectionData.authInfo &&
                      props.selectedResource.connectionData.authInfo.parameters
                        ? props.selectedResource.connectionData.authInfo.parameters
                        : null
                    }
                    connectionData={props?.selectedResource?.connectionData}
                    onValidationChange={setAuthFormValid}
                  />
                );
              case "cookie":
                return (
                  <SessionBaseAuthForm
                    handleChangeValues={(params) => {
                      setAuthParams(params);
                      checkForChanges({
                        apiName,
                        apiUrl,
                        httpMethod,
                        authMethod,
                        authParams: params,
                        isAuthRequired,
                        isWitmegApi,
                        visibleTo,
                        isShareable,
                        userIdList,
                        urlParams,
                        headers,
                        bodyParams,
                      });
                    }}
                    isViewOnly={props.isViewOnly}
                    authData={
                      props.selectedResource &&
                      props.selectedResource.connectionData &&
                      props.selectedResource.connectionData.authInfo &&
                      props.selectedResource.connectionData.authInfo.parameters
                        ? props.selectedResource.connectionData.authInfo.parameters
                        : null
                    }
                    onValidationChange={setAuthFormValid}
                  />
                );
              case "jwt":
                return (
                  <JWTAuthForm
                    handleChangeValues={(params) => {
                      setAuthParams(params);
                      checkForChanges({
                        apiName,
                        apiUrl,
                        httpMethod,
                        authMethod,
                        authParams: params,
                        isAuthRequired,
                        isWitmegApi,
                        visibleTo,
                        isShareable,
                        userIdList,
                        urlParams,
                        headers,
                        bodyParams,
                      });
                    }}
                    authData={
                      props.selectedResource &&
                      props.selectedResource.connectionData &&
                      props.selectedResource.connectionData.authInfo &&
                      props.selectedResource.connectionData.authInfo.parameters
                        ? props.selectedResource.connectionData.authInfo.parameters
                        : null
                    }
                    isViewOnly={props.isViewOnly}
                    onValidationChange={setAuthFormValid}
                  />
                );
              default:
                return <div></div>;
            }
          })()}
        </div>

        {!props.isViewOnly && (
          <>
            <Divider />
            <div
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <Button
                  // htmlType="submit"
                  onClick={testRestApi}
                  loading={apiTestStatus === "pending"}
                  disabled={apiName === "" || apiUrl === ""}
                >
                  Test APIs
                </Button>
                <Button
                  type="primary"
                  // htmlType="submit"
                  onClick={handleSaveRestApi}
                  loading={apiSaveStatus === "pending"}
                  disabled={(authMethod !== "none" && !authFormValid) || (isEditMode && !hasChanges)}
                >
                  {isEditMode ? "Update Resource" : "Create Resource"}
                </Button>
              </div>

              {(() => {
                switch (apiTestStatus) {
                  case "success":
                    return <Alert message="Test Connection Success" type="success" showIcon />;
                  case "error":
                    return <Alert message="Test Connection Failed" type="error" showIcon />;
                  case "pending":
                    return (
                      <div>
                        <Spin />
                      </div>
                    );
                  default:
                    return <div></div>;
                }
              })()}

              {(() => {
                switch (apiSaveStatus) {
                  case "success":
                    return <Alert message="Saving API data Success" type="success" showIcon />;
                  case "error":
                    return <Alert message="Saving API data Failed" type="error" showIcon />;
                  case "pending":
                    return (
                      <div>
                        <Spin />
                      </div>
                    );
                  default:
                    return <div></div>;
                }
              })()}
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default RestApiForm;
